<template>
  <!-- Hours table list -->
  <div class="invoice-import-hours" :class="{ hiddentable: !displayTable }">
    <div class="import-filters">

      <FilterBar 
        :cookieName="filterCookieName" 
        :filter-bar-type="'hours'"
        @change="filtersChanged" 
        :mainFilters="['DateRangeFilter', 'UserFilter', 'ImportHoursProjectFilter']"
        class="w-100"
      >
        <template v-slot:right-bar>
          <div class="" style="display:flex; gap: 0.5rem; align-items: center;">
            {{ $gettext('Aggregate items?') }}
            
            <font-awesome-icon id="tooltip-aggregate" class="tooltip-icon tooltip-icon--question" icon="fa-regular fa-circle-question" />
            <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-aggregate" triggers="hover">
              <div class="tooltip-title">
                {{ $gettext('You can aggregate your hours in the following ways:') }}
              </div>
              <div class="tooltip-text">
                {{ $gettext('All: will aggregate all your hours in a single line') }}<br>
                {{ $gettext('Per project: will aggregate your hours per project') }}<br>
                {{ $gettext('No grouping: will import all hour entries in separate lines.') }}
              </div>
            </b-tooltip>

            <ImportAggregateHours v-model="aggregationType" />
          </div>
        </template>
      </FilterBar>
      
    </div>
    <div class="selected-items-wrap">
      <overlay-scrollbars :options="{ scrollbars: { autoHide: 'never' }, overflowBehavior: { y: 'scroll', x: 'hidden' } }">

        <b-table 
          :items="groupedItems"
          :fields="selectedFields"
          class="import-selected-items-table"
          :class="['gk-table gk-table--responsive gk-table--headed gk-table--striped']"
          show-empty
        >
          <template #empty="">
            {{ $gettext('No items selected to import')}}
          </template>
          <template #cell(description)="row">
            {{ row.item.description }}
          </template>
          <template #cell(total_hours)="row">
            {{ row.item.multiplier | formatNumberDecimal(currentCompany.hours_rounding_precision) }}
          </template>
          <template #cell(hourly_rate)="row">
            <span class="gk-input-light">
              <b-input-group prepend="€" class="icon-contained-input-group">
                <b-form-input v-model="row.item.amount" type="number" required />
              </b-input-group>
            </span>
          </template>
          <template #cell(total)="row">
            <span class="text-nowrap">
              € {{ (row.item.amount * row.item.multiplier) | formatNumber }}
            </span>
          </template>
        </b-table>

      </overlay-scrollbars>
    </div>
    <div class="import-actions-bar">
      <GekkoButton 
        @click="displayTable = !displayTable"
        :text="$gettext('Selected hours') + ' ' + '(' + selectedHours.length + '/' + itemCount + ')'"
        :icon="(displayTable) ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"
        icon-right
        variant="link"
        class="px-0"
      />
      <div class="import-actions-bar--buttons">
        <div style="margin-right: 1rem; font-size: 14px;">
          {{ $gettext('Import a total of') }} {{ groupedItems.length }} {{ $gettext('lines') }}
        </div>
        <GekkoButton 
          @click="$emit('close', false)"
          :text="$gettext('Cancel')"
          variant="light"
        />
        <GekkoButton 
          @click="$emit('add-lines', groupedItems)"
          :text="(isQuotation) ? $gettext('Add to quotation') : $gettext('Add to invoice')"
        />
      </div>
    </div>
    <div class="available-items-wrap">
      <HourListTable
        ref="hours-table"
        component-on-modal
        selectable-rows
        list-all
        :show-top-actions="false"
        :excludeTableFields="['action']"
        @selected-item="selectedHoursTableItems"
        @get-hours="determineCheckedHours"
        @destroyed="hoursTableDestroyed"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import FilterBar from "@/components/list_filters/FilterBar"
import GekkoButton from "@/components/GekkoButton"
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import HourListTable from "@/components/hours/HourListTable"
import ImportAggregateHours from "@/components/invoices/ui/ImportAggregateHours";

export default {
  name: "ImportHoursToInvoice",
  components: {
    GekkoButton,
    LoadingSpinner,
    HourListTable,
    FilterBar,
    ImportAggregateHours
  },
  props: {
    filterCookieName: {
      type: String,
      default: ''
    },
    invoiceLines: {
      type: Array,
      default: () => []
    },
    isQuotation: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loading: true,
      groupedItems: [],
      selectedHours: [],
      hourList: [],
      itemCount: 0,
      aggregationType: null,
      displayTable: false,
      initFilters: false,
      customerHourlyRate: null,
      selectedFields: [
        { key: "description", label: this.$gettext('Description'), class: 'w-75' },
        { key: "total_hours", label: this.$gettext('Total hours'), class: 'w-25 tablecol-sm text-right' },
        { key: "hourly_rate", label: this.$gettext('Hourly rate (Ex. VAT)'), class: 'w-25 tablecol-sm text-right' },
        { key: "total", label: this.$gettext('Total'), class: 'w-25 tablecol-sm text-right' },
      ]
    }
  },
  computed: {
    ...mapGetters({ 
      currentCompany: "main/getCurrentCompany",
      selectedCustomer: "invoices/getInvoiceCreateRecipient",
    })
  },
  watch: {
    aggregationType (newValue) {
      this.groupBy()

      // set cookie
      const d1 = new Date ()
      const expire = new Date (d1)
      expire.setMinutes (d1.getMinutes() + 60 * 12)
      this.$cookies.set('import_hours_grouping_v2', newValue, expire, '/')
    },
    selectedHours () {
      this.groupBy()
    }
  },
  created () {
    this.aggregationType = this.$cookies.get('import_hours_grouping_v2') || 'per_project'

    this.groupBy()
    
    this.customerHourlyRate = parseFloat(this.selectedCustomer.hourly_rate)
  },
  mounted () {
    const modalEl = document.getElementsByClassName('gk-modal-wrapper')[0]
    modalEl.addEventListener('scroll', this.handleScroll)
  },
  methods: {
    selectedHoursTableItems () {
      this.refreshSelectedList()
    },
    hoursTableDestroyed () {
      const modalEl = document.getElementsByClassName('gk-modal-wrapper')[0]
      if (modalEl) {
        modalEl.removeEventListener('scroll', this.handleScroll)
      }
    },
    determineCheckedHours (item_count) {
      this.itemCount = item_count
      this.hourList = this.$refs['hours-table'].hourListRender

      let invoice_lines = [];
      if (this.invoiceLines) {
        invoice_lines = this.invoiceLines;
      }

      for (let i = 0; i < this.hourList.length; i++) {
        for (let j = 0; j < invoice_lines.length; j++) {
          if (invoice_lines[j].aggregated_hours && invoice_lines[j].aggregated_hours.indexOf(this.hourList[i].id) != -1) {
            this.hourList[i].is_invoiced = true
            break;
          }
        }
      }

      for (let i = 0; i < this.hourList.length; i++) {
        this.hourList[i].selected = !this.hourList[i].is_invoiced
      }
      this.refreshSelectedList()
    },
    refreshSelectedList () {
      this.selectedHours = this.$refs['hours-table'].selectedTableItems
    },
    /**
     * Groups items and formats output for invoice lines 
     * invoice lines containing aggregated items
     * should have an array with correspondant item ids
     *    COSTS -> (array) invoice_line.cost_ids
     *    HOURS -> (array) invoice_line.aggregated_hours
     *    TRIPS -> (array) invoice_line.trip_ids
     *    PRODUCTS -> (number) invoice_line.product - cannot be grouped, just the ID
     * 
     * @returns {array} group of items formatted, to be added as invoice lines
     */
    groupBy () {
      const items = this.selectedHours
      const grouped_items = items.reduce((acc_items, item) => {

        let key
        if (!this.aggregationType || this.aggregationType === 'no') {
          key = item.id
        } else if (this.aggregationType === 'per_project') {
          key = item.created_by.id + "_" + item.project.id 
        } else if (this.aggregationType === 'all') {
          key = null
        }

        const existing_group = acc_items.find(group => {
          let match = false
          if (this.aggregationType === 'per_project' && (group.created_by.id + '_' + group.project.id === key)) {
            match = group
          } else if (this.aggregationType === 'all') {
            match = group
          }
          return match
        })
        
        // Update group values
        if (existing_group) {

          // Multiplier
          existing_group.multiplier += this.roundFloat(item.amount)

          // Save item id
          existing_group.aggregated_hours.push(item.id)
          
          // Set first and last date for item description
          existing_group.first_date = Math.min(new Date(existing_group.first_date), new Date(item.date));
          existing_group.last_date = Math.max(new Date(existing_group.last_date), new Date(item.date));
          
        } else {
          // Push a new group
          const group_first_item = {
            grouped_by: { type: this.aggregationType, id: key },
            aggregated_hours: [item.id],
            multiplier: this.roundFloat(item.amount),
            created_by: item.created_by,
            project: item.project,
            amount: parseFloat(this.get_current_hourly_rate(item.project)),
            description: item.description,
            tax_rate: this.currentCompany.default_tax_rate,
            vat_exempt: false,
            first_date: item.date,
            last_date: item.date,
          }

          acc_items.push(group_first_item);
        }
        return acc_items;
      }, []);

      // Format the descriptions
      grouped_items.forEach(grouped_item => {
        grouped_item.first_date = new Date(grouped_item.first_date).toISOString().split('T')[0]
        grouped_item.last_date = new Date(grouped_item.last_date).toISOString().split('T')[0]
        if (this.aggregationType === 'all') {
          grouped_item.description = this.$gettext('Hours') + ' ' + grouped_item.created_by.display_name + ' ' + 
                                      this.$gettext('from') + ' ' + grouped_item.first_date + ' ' + 
                                      this.$gettext('until') + ' ' + grouped_item.last_date;
        } else if (this.aggregationType === 'per_project') {
          grouped_item.description = this.$gettext('Hours') + ' ' + grouped_item.created_by.display_name + ' ' + 
                            this.$gettext('for') + ' ' + grouped_item.project.name + ' ' +
                            this.$gettext('from') + ' ' + grouped_item.first_date + ' ' + 
                            this.$gettext('until') + ' ' + grouped_item.last_date;
        } else {
          grouped_item.description = this.$gettext('Hours') + ' ' + grouped_item.created_by.display_name + ' ' + 
                  this.$gettext('on') + ' ' + grouped_item.first_date + ': ' + grouped_item.description;
        }
        delete grouped_item.first_date;
        delete grouped_item.last_date;
      })

      this.groupedItems = grouped_items
    },
    filtersChanged (value) {
      if (value) this.initFilters = value
      if (this.$refs['hours-table']) {
        this.$refs['hours-table'].getHourList()
      }
    },
    handleScroll () {
      this.$refs['hours-table'].handleScroll()
    },
    roundFloat (input) {
      /**
       * Round the hours entries to 1 decimal (default), 2 or 0;
       */
      let fractionSize = 1
      if (this.currentCompany && this.currentCompany.hours_rounding_precision in [0, 1, 2]) {
        fractionSize = parseInt(this.currentCompany.hours_rounding_precision)
      }
      const roundedInput = parseFloat(input).toFixed(fractionSize)
      return parseFloat(roundedInput)
    },
    get_current_hourly_rate (project) {
      if (this.aggregationType === 'all') {
        return this.get_hourly_rate(1, project)
      } else { // both PER_PROJECT and NO_GROUPING are treated the same
        return this.get_hourly_rate(0, project)
      }
    },
    get_hourly_rate (rate_level, project) {
      if (rate_level === 0) {
        if (project && project.hourly_rate != null && !isNaN(project.hourly_rate)) {
          return project.hourly_rate
        } else {
          return this.get_hourly_rate(1, project)
        }
      }
      if (rate_level === 1) {
        if (this.customerHourlyRate != null && !isNaN(this.customerHourlyRate)) {
          return this.customerHourlyRate
        } else {
          return this.get_hourly_rate(2, project)
        }
      }
      if (rate_level === 2) {
        if (this.currentCompany.hourly_rate != null && !isNaN(this.currentCompany.hourly_rate)) {
          return this.currentCompany.hourly_rate
        }
      }
      return 0;
    }
  }
};
</script>

<style lang="scss" scoped>

.invoice-import-hours {
  display: flex;
  flex-direction: column;
  // gap: 1rem;
  
  .import-filters {
    display: flex;
    width: 100%;
    justify-content: space-between;
    padding-top: 0.5rem;
  }
  .import-actions-bar {
    display: flex;
    justify-content: space-between;
    // gap: 0.5rem;
    align-items: center;
    padding: 1rem 0;
    &--buttons {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      align-items: center;
    }
  }
  .selected-items-wrap {
    height: 300px;
  }
  &.hiddentable {
    // margin-bottom: -1rem;
    .import-actions-bar {
      padding-bottom: 0;
    }
    .available-items-wrap {
        height: 0;
        overflow: hidden;
    }
  }
}

</style>

<style lang="scss">

</style>